import React, { useState, useEffect } from 'react';
import { userAPI } from '../../lib/api';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Trophy, Medal, Clock, Calendar, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

export function RankingsPage() {
  const [rankings, setRankings] = useState([]);
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('overall'); // 'overall', 'quiz', 'daily', 'league'
  const [selectedQuiz, setSelectedQuiz] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    loadRankings();
  }, [view, selectedQuiz]);

  const loadRankings = async () => {
    setLoading(true);
    try {
      if (view === 'league') {
        const res = await userAPI.getCurrentLeague();
        setLeague(res.data);
        setRankings(res.data.standings || []);
      } else {
        let res;
        if (view === 'quiz') {
          res = await userAPI.getQuizRankings(selectedQuiz);
        } else if (view === 'daily') {
          res = await userAPI.getDailyRankings();
        } else {
          res = await userAPI.getRankings();
        }
        setRankings(res.data.rankings || []);
      }
    } catch (error) {
      toast.error(t('rankings.loadFailed'));
      setRankings([]);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <span className="text-2xl">🥇</span>;
    if (rank === 2) return <span className="text-2xl">🥈</span>;
    if (rank === 3) return <span className="text-2xl">🥉</span>;
    return <span className="text-lg font-bold text-slate-500">#{rank}</span>;
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-6">
      {/* Header */}
      <div className="text-center mb-6">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl sm:text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3"
        >
          <Trophy className="w-7 h-7 sm:w-8 sm:h-8 text-yellow-400" />
          {t('rankings.title')}
        </motion.h1>
        <p className="text-slate-400 text-sm sm:text-base">{t('rankings.subtitle')}</p>
      </div>

      {/* View Tabs */}
      <div className="bg-slate-800/50 rounded-2xl p-2 border border-slate-700/50">
        <div className="flex gap-2">
          {[
            { id: 'overall', label: t('rankings.overall'), icon: Trophy },
            { id: 'quiz', label: t('rankings.perQuiz'), icon: Medal },
            { id: 'daily', label: t('rankings.daily'), icon: Calendar },
            { id: 'league', label: t('rankings.league', 'League'), icon: Users }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium transition-all ${
                view === tab.id
                  ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Quiz Selector (for per-quiz view) */}
      {view === 'quiz' && (
        <div className="flex flex-wrap gap-2 justify-center">
          {[...Array(11)].map((_, i) => (
            <button
              key={i}
              onClick={() => setSelectedQuiz(i)}
              className={`w-10 h-10 rounded-lg font-bold transition-all ${
                selectedQuiz === i
                  ? 'bg-teal-500 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {i === 10 ? '⭐' : i + 1}
            </button>
          ))}
        </div>
      )}

      {/* League header */}
      {view === 'league' && league && !loading && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-slate-800/80 to-slate-800/40 rounded-2xl border border-slate-700/50 p-5 flex flex-col sm:flex-row items-center gap-4">
          <div className="text-5xl">{league.my_tier?.icon}</div>
          <div className="flex-1 text-center sm:text-left">
            <p className="text-lg font-bold text-white">{league.my_tier?.name} {t('rankings.leagueTitle', 'League')}</p>
            <p className="text-sm text-slate-400">
              {t('rankings.weeklyXp', 'This week')}: <span className="text-teal-400 font-semibold">{league.my_xp_week} XP</span>
              {league.my_rank ? <span className="ml-2">• #{league.my_rank}</span> : null}
            </p>
            {league.next_tier && (
              <div className="mt-2">
                <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-teal-400 to-cyan-400"
                    style={{ width: `${Math.min(100, Math.round((league.my_xp_week - (league.my_tier?.min_xp || 0)) / Math.max(1, (league.next_tier.min_xp - (league.my_tier?.min_xp || 0))) * 100))}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {league.xp_to_next} XP {t('rankings.toTier', 'to')} {league.next_tier.icon} {league.next_tier.name}
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {(league.tiers || []).map(tier => (
              <span key={tier.id} title={`${tier.name} ${tier.min_xp}+ XP`}
                className={`text-xl ${tier.id === league.my_tier?.id ? '' : 'opacity-30 grayscale'}`}>{tier.icon}</span>
            ))}
          </div>
        </motion.div>
      )}

      {/* Rankings List */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : rankings.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">{t('rankings.noRankings')}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {rankings.map((entry, idx) => (
              <motion.div
                key={entry.user_id || idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className={`flex items-center gap-4 p-4 ${
                  entry.is_current_user ? 'bg-teal-500/10' : 'hover:bg-slate-800/50'
                }`}
              >
                {/* Rank */}
                <div className="w-12 text-center">
                  {getRankIcon(entry.rank)}
                </div>

                {/* User Info */}
                <div className="flex-1">
                  <p className={`font-semibold ${
                    (entry.is_current_user || entry.is_me) ? 'text-teal-400' : 'text-white'
                  }`}>
                    {entry.nickname}
                    {(entry.is_current_user || entry.is_me) && <span className="ml-2 text-xs bg-teal-500/20 px-2 py-1 rounded-full">{t('rankings.you')}</span>}
                  </p>
                  {view === 'daily' && entry.quizzes_completed !== undefined && (
                    <p className="text-xs text-slate-500">
                      {entry.quizzes_completed} {t('rankings.quizzesCompleted')}
                    </p>
                  )}
                </div>

                {/* Score */}
                <div className="text-right">
                  <p className="font-bold text-teal-400">
                    {view === 'league'
                      ? `${entry.xp_week || 0} XP`
                      : entry.percentage !== undefined
                      ? `${Number(entry.percentage).toFixed(2)}%`
                      : entry.avg_percentage !== undefined
                      ? `${Number(entry.avg_percentage).toFixed(2)}%`
                      : view === 'daily' 
                      ? `${entry.total_correct || 0}/${entry.total_questions || 0}`
                      : `${entry.correct_count || entry.total_correct || 0}/${entry.total || entry.total_questions || 30}`
                    }
                  </p>
                  {(entry.time_ms || entry.total_time_ms) && (
                    <p className="text-xs text-slate-500 flex items-center justify-end gap-1">
                      <Clock className="w-3 h-3" />
                      {((entry.time_ms || entry.total_time_ms) / 1000).toFixed(1)}s
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
